/**
 * Auth API Types
 * Zod schemas for Strapi authentication endpoints
 */

import { z } from '@ufc/core'

// ============================================================================
// User DTO Schema
// ============================================================================

export const tenantSummarySchema = z.object({
  id: z.union([z.number(), z.string()]),
  documentId: z.string().optional(),
  name: z.string().optional(),
  code: z.string().optional(),
  tenantStatus: z.string().optional(),
  channel: z.enum(['b2b', 'ecommerce']).optional(),
});

/**
 * Role schema for Strapi users-permissions plugin
 */
export const roleDtoSchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  name: z.string(),
  description: z.string().optional(),
  type: z.string(),
})

export type RoleDto = z.infer<typeof roleDtoSchema>

export const factoryLocationSchema = z.object({
  documentId: z.string(),
})

export type FactoryLocationDto = z.infer<typeof factoryLocationSchema>

export const userLocationSchema = z.object({
  documentId: z.string(),
})

export type UserLocationDto = z.infer<typeof userLocationSchema>

export const userDtoSchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  username: z.string(),
  email: z.string().email(),
  provider: z.string().optional(),
  confirmed: z.boolean(),
  blocked: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  fullName: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  companyName: z.string().nullable().optional(),
  tenant: tenantSummarySchema.optional(),
  role: roleDtoSchema.optional(),
  /** Location of the factory assigned to this user (security_guard only) */
  factoryLocation: factoryLocationSchema.nullable().optional(),
  /** Direct user→location (fallback when no factory) */
  userLocation: userLocationSchema.nullable().optional(),
  /** documentId of the factory assigned to this user (scale roles, safety_manager, etc.) */
  factoryDocumentId: z.string().nullable().optional(),
})

export type UserDto = z.infer<typeof userDtoSchema>
export type TenantSummaryDto = z.infer<typeof tenantSummarySchema>

// ============================================================================
// Auth Response Schemas
// ============================================================================

/**
 * Login/Register response from Strapi
 */
export const authResponseSchema = z.object({
  jwt: z.string(),
  user: userDtoSchema,
})

export type AuthResponseDto = z.infer<typeof authResponseSchema>

/**
 * Error response from Strapi
 */
export const authErrorSchema = z.object({
  data: z.null().optional(),
  error: z.object({
    status: z.number(),
    name: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
})

export type AuthErrorDto = z.infer<typeof authErrorSchema>

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Login request
 */
export const loginRequestSchema = z.object({
  identifier: z.string().min(1, 'Email or username is required'),
  password: z.string().min(1, 'Password is required'),
  turnstileToken: z.string().optional(),
})

export type LoginRequestDto = z.infer<typeof loginRequestSchema>

/**
 * Register request
 */
export const registerRequestSchema = z.object({
  username: z.string().min(3, 'Username must be at least 3 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  fullName: z.string().min(1, 'Full name is required'),
  companyName: z.string().min(1, 'Company name is required'),
  mobilePhone: z.string().min(4, 'Mobile phone is required'),
  telephone: z.string().optional(),
  vatNumber: z.string().min(1, 'VAT number is required'),
  addressLine1: z.string().min(1, 'Address is required'),
  houseNumber: z.string().max(4).optional(),
  city: z.string().min(1, 'City is required'),
  region: z.string().min(1, 'Region is required'),
  postalCode: z.string().min(1, 'Postal code is required'),
})

export type RegisterRequestDto = z.infer<typeof registerRequestSchema>

/**
 * Register response (supports custom pending response or default Strapi auth response)
 */
export const registerResponseSchema = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  user: userDtoSchema,
  tenant: tenantSummarySchema.optional(),
  jwt: z.string().optional(),
})

export type RegisterResponseDto = z.infer<typeof registerResponseSchema>

/**
 * Forgot password request
 */
export const forgotPasswordRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
})

export type ForgotPasswordRequestDto = z.infer<typeof forgotPasswordRequestSchema>

/**
 * Reset password request
 */
export const resetPasswordRequestSchema = z.object({
  code: z.string().min(1, 'Reset code is required'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  passwordConfirmation: z.string().min(6, 'Password confirmation is required'),
}).refine(data => data.password === data.passwordConfirmation, {
  message: 'Passwords do not match',
  path: ['passwordConfirmation'],
})

export type ResetPasswordRequestDto = z.infer<typeof resetPasswordRequestSchema>

/**
 * Change password request
 */
export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  passwordConfirmation: z.string().min(6, 'Password confirmation is required'),
}).refine(data => data.password === data.passwordConfirmation, {
  message: 'Passwords do not match',
  path: ['passwordConfirmation'],
})

export type ChangePasswordRequestDto = z.infer<typeof changePasswordRequestSchema>

export const changePasswordOtpRequestSchema = z.object({
  channel: z.enum(['email', 'phone']),
  code: z.string().regex(/^\d{6}$/, 'OTP code must be a 6-digit number'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  passwordConfirmation: z.string().min(6, 'Password confirmation is required'),
}).refine(data => data.password === data.passwordConfirmation, {
  message: 'Passwords do not match',
  path: ['passwordConfirmation'],
})

export type ChangePasswordOtpRequestDto = z.infer<typeof changePasswordOtpRequestSchema>

export const requestChangePasswordOtpRequestSchema = z.object({
  channel: z.enum(['email', 'phone']),
})

export type RequestChangePasswordOtpRequestDto = z.infer<typeof requestChangePasswordOtpRequestSchema>

/**
 * Delete account request
 */
export const deleteAccountRequestSchema = z.object({
  deletionReason: z.string().max(500, 'Deletion reason must be 500 characters or less').optional(),
})

export type DeleteAccountRequestDto = z.infer<typeof deleteAccountRequestSchema>

/**
 * Update profile request
 */
export const updateProfileRequestSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  phone: z.string().min(1, 'Phone is required'),
})

export type UpdateProfileRequestDto = z.infer<typeof updateProfileRequestSchema>

/**
 * Forgot password response
 */
export const forgotPasswordResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string().optional(),
})

export type ForgotPasswordResponseDto = z.infer<typeof forgotPasswordResponseSchema>

/**
 * Reset password response (can be auth response or just message)
 */
export const resetPasswordResponseSchema = z.object({
  jwt: z.string().optional(),
  user: userDtoSchema.optional(),
  message: z.string().optional(),
})

export type ResetPasswordResponseDto = z.infer<typeof resetPasswordResponseSchema>

/**
 * Change password response
 */
export const changePasswordResponseSchema = z.object({
  jwt: z.string(),
  user: userDtoSchema,
  message: z.string().optional(),
})

export type ChangePasswordResponseDto = z.infer<typeof changePasswordResponseSchema>

export const requestChangePasswordOtpResponseSchema = z.object({
  ok: z.boolean(),
  channel: z.enum(['email', 'phone']),
  message: z.string().optional(),
})

export type RequestChangePasswordOtpResponseDto = z.infer<typeof requestChangePasswordOtpResponseSchema>

/**
 * Delete account response
 */
export const deleteAccountResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string().optional(),
})

export type DeleteAccountResponseDto = z.infer<typeof deleteAccountResponseSchema>

/**
 * Update profile response
 */
export const updateProfileResponseSchema = userDtoSchema

export type UpdateProfileResponseDto = z.infer<typeof updateProfileResponseSchema>

// ============================================================================
// Strapi Auth Endpoints
// ============================================================================

export const STRAPI_AUTH_ENDPOINTS = {
  LOGIN: '/api/auth/local',
  REGISTER: '/api/auth/local/register',
  FORGOT_PASSWORD: '/api/auth/forgot-password',
  RESET_PASSWORD: '/api/auth/reset-password',
  CHANGE_PASSWORD: '/api/auth/change-password',
  CHANGE_PASSWORD_REQUEST_OTP: '/api/auth/request-change-password-otp',
  DELETE_ACCOUNT: '/api/auth/delete-account',
  UPDATE_PROFILE: '/api/auth/update-profile',
  ME: '/api/users/me',
} as const

// ============================================================================
// Validation Helpers
// ============================================================================

export function validateLoginRequest(data: unknown): LoginRequestDto {
  return loginRequestSchema.parse(data)
}

export function validateRegisterRequest(data: unknown): RegisterRequestDto {
  return registerRequestSchema.parse(data)
}

export function validateAuthResponse(data: unknown): AuthResponseDto {
  return authResponseSchema.parse(data)
}

export function validateRegisterResponse(data: unknown): RegisterResponseDto {
  return registerResponseSchema.parse(data)
}

export function validateForgotPasswordResponse(data: unknown): ForgotPasswordResponseDto {
  return forgotPasswordResponseSchema.parse(data)
}

export function validateResetPasswordResponse(data: unknown): ResetPasswordResponseDto {
  return resetPasswordResponseSchema.parse(data)
}

export function validateChangePasswordRequest(data: unknown): ChangePasswordRequestDto {
  return changePasswordRequestSchema.parse(data)
}

export function validateChangePasswordOtpRequest(data: unknown): ChangePasswordOtpRequestDto {
  return changePasswordOtpRequestSchema.parse(data)
}

export function validateChangePasswordResponse(data: unknown): ChangePasswordResponseDto {
  return changePasswordResponseSchema.parse(data)
}

export function validateRequestChangePasswordOtpRequest(data: unknown): RequestChangePasswordOtpRequestDto {
  return requestChangePasswordOtpRequestSchema.parse(data)
}

export function validateRequestChangePasswordOtpResponse(data: unknown): RequestChangePasswordOtpResponseDto {
  return requestChangePasswordOtpResponseSchema.parse(data)
}

export function validateDeleteAccountRequest(data: unknown): DeleteAccountRequestDto {
  return deleteAccountRequestSchema.parse(data)
}

export function validateDeleteAccountResponse(data: unknown): DeleteAccountResponseDto {
  return deleteAccountResponseSchema.parse(data)
}

export function validateUpdateProfileRequest(data: unknown): UpdateProfileRequestDto {
  return updateProfileRequestSchema.parse(data)
}

export function validateUpdateProfileResponse(data: unknown): UpdateProfileResponseDto {
  return updateProfileResponseSchema.parse(data)
}
